import { useState, useEffect } from 'react'
import { Line, Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend } from 'chart.js'
import { getHistorique, getVentesMensuelles, CONCESSIONS, MOIS_FR } from '../lib/supabase'
import './Dashboard.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend)

const ANNEE_COURANTE = new Date().getFullYear()

export default function Comparatifs() {
  const [historique, setHistorique] = useState([])
  const [ventesCourantes, setVentesCourantes] = useState([])
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState('monthly')
  const [categorie, setCategorie] = useState('total')

  useEffect(() => {
    async function load() {
      const [hist, current] = await Promise.all([
        getHistorique(2016, ANNEE_COURANTE),
        getVentesMensuelles(ANNEE_COURANTE)
      ])
      setHistorique(hist)
      setVentesCourantes(current)
      setLoading(false)
    }
    load()
  }, [])

  function getTotal(annee, mois, cat) {
    // Priorité aux saisies journalières pour l'année courante
    if (annee === ANNEE_COURANTE) {
      const rows = ventesCourantes.filter(r => r.mois === mois)
      const neuf = rows.reduce((s,r) => s+r.neuf, 0)
      const occ  = rows.reduce((s,r) => s+r.occasion, 0)
      if (cat === 'neuf') return neuf
      if (cat === 'occ')  return occ
      return neuf + occ
    }
    const rows = historique.filter(h => h.annee === annee && (mois ? h.mois === mois : true))
    const neuf = rows.reduce((s,r) => s+r.neuf, 0)
    const occ  = rows.reduce((s,r) => s+r.occasion, 0)
    if (cat === 'neuf') return neuf
    if (cat === 'occ')  return occ
    return neuf + occ
  }

  function getQuart(annee, q, cat) {
    const moisQuart = [[1,2,3],[4,5,6],[7,8,9],[10,11,12]]
    return moisQuart[q-1].reduce((s,m) => s + getTotal(annee, m, cat), 0)
  }

  const chartBaseOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { color: '#999', font: { size: 11 } } },
      y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { color: '#999', font: { size: 11 } } }
    }
  }

  if (loading) return <div className="loading">Chargement de l'historique…</div>

  const moisLabels = MOIS_FR.slice(1).map(m => m.substring(0,3))
  const quartLabels = ['Q1 (jan-mars)', 'Q2 (avr-juin)', 'Q3 (juil-sept)', 'Q4 (oct-déc)']

  let tableRows = []
  let chartData = {}

  if (type === 'monthly') {
    const annees = [ANNEE_COURANTE - 1, ANNEE_COURANTE]
    tableRows = moisLabels.map((label, i) => {
      const mois = i + 1
      const vals = annees.map(a => getTotal(a, mois, categorie))
      const v = vals[0] ? Math.round((vals[1]-vals[0])/vals[0]*100) : null
      return { label, vals, variation: v }
    })
    chartData = {
      labels: moisLabels,
      datasets: [
        {
          label: String(ANNEE_COURANTE),
          data: moisLabels.map((_,i) => getTotal(ANNEE_COURANTE, i+1, categorie)),
          borderColor: '#C8102E', backgroundColor: 'rgba(200,16,46,0.08)', tension: 0.3, pointRadius: 4, fill: true
        },
        {
          label: String(ANNEE_COURANTE-1),
          data: moisLabels.map((_,i) => getTotal(ANNEE_COURANTE-1, i+1, categorie)),
          borderColor: '#bbb', backgroundColor: 'transparent', tension: 0.3, pointRadius: 3, borderDash: [4,3]
        }
      ]
    }
  } else if (type === 'quarterly') {
    const annees = [ANNEE_COURANTE - 1, ANNEE_COURANTE]
    tableRows = [1,2,3,4].map(q => {
      const vals = annees.map(a => getQuart(a, q, categorie))
      const v = vals[0] ? Math.round((vals[1]-vals[0])/vals[0]*100) : null
      return { label: quartLabels[q-1], vals, variation: v }
    })
    chartData = {
      labels: ['Q1','Q2','Q3','Q4'],
      datasets: annees.map((a, idx) => ({
        label: String(a),
        data: [1,2,3,4].map(q => getQuart(a, q, categorie)),
        backgroundColor: idx===1 ? '#C8102E' : '#ddd',
        borderRadius: 3
      }))
    }
  } else {
    // Annuel 2016-courant
    const annees = []
    for (let a = 2016; a <= ANNEE_COURANTE; a++) annees.push(a)
    tableRows = annees.map((a, i) => {
      const val = getTotal(a, null, categorie)
      const prev = i > 0 ? getTotal(annees[i-1], null, categorie) : null
      const v = prev ? Math.round((val-prev)/prev*100) : null
      return { label: String(a), vals: [val], variation: v, single: true }
    })
    chartData = {
      labels: annees.map(String),
      datasets: [{
        label: 'Total annuel',
        data: annees.map(a => getTotal(a, null, categorie)),
        backgroundColor: annees.map((a,i) => i === annees.length-1 ? '#C8102E' : '#ddd'),
        borderRadius: 4
      }]
    }
  }

  const catLabel = { total: 'Neufs + Occasion', neuf: 'Neufs', occ: 'Occasion' }[categorie]
  const colHeaders = type === 'monthly'
    ? ['Mois', String(ANNEE_COURANTE-1), String(ANNEE_COURANTE), 'Variation']
    : type === 'quarterly'
    ? ['Quart', String(ANNEE_COURANTE-1), String(ANNEE_COURANTE), 'Variation']
    : ['Année', 'Total', 'Variation']

  return (
    <div className="comparatifs-page">
      <div className="page-header">
        <div>
          <h1>Comparatifs historiques</h1>
          <p>Données depuis janvier 2016 · {catLabel}</p>
        </div>
      </div>

      <div className="comp-controls">
        <select value={type} onChange={e => setType(e.target.value)}>
          <option value="monthly">Mensuel {ANNEE_COURANTE} vs {ANNEE_COURANTE-1}</option>
          <option value="quarterly">Par quart</option>
          <option value="yearly">Annuel (2016-{ANNEE_COURANTE})</option>
        </select>
        <select value={categorie} onChange={e => setCategorie(e.target.value)}>
          <option value="total">Neufs + Occasion</option>
          <option value="neuf">Neufs seulement</option>
          <option value="occ">Occasion seulement</option>
        </select>
      </div>

      <div className="chart-card" style={{marginBottom:'1.5rem'}}>
        <div className="chart-header">
          <h3>{type === 'yearly' ? `Évolution annuelle 2016–${ANNEE_COURANTE}` : type === 'quarterly' ? 'Comparatif par quart' : `Comparatif mensuel ${ANNEE_COURANTE} vs ${ANNEE_COURANTE-1}`} — {catLabel}</h3>
          {type !== 'yearly' && (
            <div className="chart-legend">
              <span><span className="leg-dot" style={{background:'#C8102E'}}/>  {ANNEE_COURANTE}</span>
              <span><span className="leg-dot" style={{background:'#bbb'}}/> {ANNEE_COURANTE-1}</span>
            </div>
          )}
        </div>
        <div style={{height: 260}}>
          {(type === 'quarterly' || type === 'yearly')
            ? <Bar data={chartData} options={chartBaseOpts} />
            : <Line data={chartData} options={chartBaseOpts} />
          }
        </div>
      </div>

      <div className="section-title">Tableau détaillé</div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>{colHeaders.map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {tableRows.map(row => (
              <tr key={row.label}>
                <td>{row.label}</td>
                {row.single
                  ? <td className="num">{row.vals[0].toLocaleString('fr-CA')}</td>
                  : row.vals.map((v,i) => <td key={i} className="num">{v ? v.toLocaleString('fr-CA') : '—'}</td>)
                }
                <td className={`num ${row.variation > 0 ? 'pos' : row.variation < 0 ? 'neg' : ''}`}>
                  {row.variation !== null ? `${row.variation > 0 ? '+' : ''}${row.variation}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section-title">Meilleure année par concession</div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Concession</th><th>Meilleure année</th><th>Total (neufs)</th><th>Total (occasion)</th></tr>
          </thead>
          <tbody>
            {CONCESSIONS.map(c => {
              let bestAnneN = null, bestN = 0, bestAnneO = null, bestO = 0
              for (let a = 2016; a <= ANNEE_COURANTE - 1; a++) {
                const rows = historique.filter(h => h.annee === a && h.concession === c.id)
                const n = rows.reduce((s,r) => s+r.neuf, 0)
                const o = rows.reduce((s,r) => s+r.occasion, 0)
                if (n > bestN) { bestN = n; bestAnneN = a }
                if (o > bestO) { bestO = o; bestAnneO = a }
              }
              return (
                <tr key={c.id}>
                  <td>
                    <span className="conc-name">{c.label}</span>
                    <span className={`brand-tag tag-${c.marque.toLowerCase()}`}>{c.marque}</span>
                  </td>
                  <td>{bestAnneN || '—'}</td>
                  <td className="num">{bestN || '—'}</td>
                  <td className="num">{bestO || '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
